import React, { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, X, Film, Music, MapPin, Sparkles, Lock, Crown, Wand2, Loader2, Mic, BookOpen, ChevronRight, Radio, Phone, Calendar } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import BackButton from '../components/BackButton';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { useToast } from '../hooks/use-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPost, createPostChunked } from '../api/posts';
import { generateTeaser } from '../api/ai';
import { useAuth } from '../context/AuthContext';
import { hasActiveCreatorProfile } from '../lib/creatorPresentation';
import ReadOnlyNotice from '../components/ReadOnlyNotice';
import { canWrite } from '../lib/canWrite';
import { creditsToEur, fmtCredits } from '../lib/currency';
import useCreatorPriceOptions from '../hooks/useCreatorPriceOptions';
import useAppConfig from '../hooks/useAppConfig';
import RaccontoAudioForm from '../components/racconti/RaccontoAudioForm';
import RaccontoTextForm from '../components/racconti/RaccontoTextForm';
// Phase F8.1 — EXACT reuse of the Live Dashboard entry points (same
// components, same rules): GoLiveNowDialog (Live Now Audio pre-flight),
// CreateLiveDialog (scheduler), OneToOneVipRequiredDialog (Chiamate gating).
import GoLiveNowDialog from '../components/GoLiveNowDialog';
import ScheduledLivesPanel from '../components/ScheduledLivesPanel';
import OneToOneVipRequiredDialog from '../components/OneToOneVipRequiredDialog';

const MAX_IMAGE_AUDIO_BYTES = 80 * 1024 * 1024;
// Phase 84 — videos get a 500MB ceiling and go through the chunked flow.
const MAX_VIDEO_BYTES = 500 * 1024 * 1024;
const CHUNKED_THRESHOLD = 20 * 1024 * 1024;
const maxBytesFor = (kind) => (kind === 'video' ? MAX_VIDEO_BYTES : MAX_IMAGE_AUDIO_BYTES);
// Phase 18.2 — caption ceiling raised to 10k; paywall teaser bounds.
const MAX_CAPTION = 10_000;
const MIN_PREVIEW = 10;
const MAX_PREVIEW = 500;

const getMediaKind = (file) => {
  if (!file) return null;
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return null;
};

const CreatePage = () => {
  const { t } = useTranslation('create');
  const { t: tLives } = useTranslation('lives');
  // Phase F6 — Racconti chooser: null = choice screen, then audio/text/photo.
  const [mode, setMode] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const scheduling = searchParams.get('mode') === 'schedule';
  // Phase F8.1 — same dialog states as LiveDashboardPage handlers.
  const [goLiveOpen, setGoLiveOpen] = useState(false);
  const [o2oGateOpen, setO2oGateOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [caption, setCaption] = useState('');
  const [location, setLocation] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  const [price, setPrice] = useState('100');
  const [previewDescription, setPreviewDescription] = useState('');
  const [generatingTeaser, setGeneratingTeaser] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  // Phase 28 — capture original media dimensions at pick time
  const [mediaDims, setMediaDims] = useState({ width: 0, height: 0 });
  const fileRef = useRef(null);
  // Scala prezzi separata per tipo di media: post_image / post_video /
  // post_audio (unione di `all` + `post` legacy + scope specifico).
  const mediaKind = getMediaKind(file);
  const { credits: priceCredits } = useCreatorPriceOptions(mediaKind ? `post_${mediaKind}` : 'post');
  useEffect(() => {
    const p = parseInt(price, 10);
    if (priceCredits.length && !priceCredits.includes(p)) {
      const firstPaid = priceCredits.find((c) => c > 0) ?? priceCredits[0];
      if (firstPaid !== undefined) setPrice(String(firstPaid));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceCredits]);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  // Phase F3 — Voice-First: hide/block video uploads when the flag is OFF.
  const { videoPostsEnabled } = useAppConfig();
  const canMonetize = !!user?.is_verified_creator;
  const needsCreatorActivation = !hasActiveCreatorProfile(user);
  const writable = canWrite(user);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const kind = getMediaKind(f);
    if (!kind) {
      toast({
        title: t('file_errors.unsupported_title'),
        description: t('file_errors.unsupported_desc'),
        variant: 'destructive',
      });
      return;
    }
    if (kind === 'video' && !videoPostsEnabled) {
      toast({
        title: 'Video disattivati',
        description: 'La pubblicazione di nuovi video è disattivata. Puoi pubblicare audio e immagini.',
        variant: 'destructive',
      });
      return;
    }
    if (f.size > maxBytesFor(kind)) {
      toast({
        title: t('file_errors.too_large_title'),
        description: t('file_errors.too_large_desc', { max: kind === 'video' ? '500 MB' : '80 MB' }),
        variant: 'destructive',
      });
      return;
    }
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreviewUrl(url);
    setMediaDims({ width: 0, height: 0 });
    // Phase 28 — extract original dimensions client-side so the feed can
    // render with the native aspect ratio.
    if (kind === 'image') {
      const img = new Image();
      img.onload = () => setMediaDims({ width: img.naturalWidth, height: img.naturalHeight });
      img.src = url;
    } else if (kind === 'video') {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.muted = true;
      v.onloadedmetadata = () => {
        setMediaDims({ width: v.videoWidth, height: v.videoHeight });
      };
      v.src = url;
      // Explicit load() — improves reliability on browsers that defer
      // metadata fetch when the element is not attached to the DOM.
      try { v.load(); } catch { /* noop */ }
    }
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFile(null);
    setCaption('');
    setLocation('');
    setIsPaid(false);
    setPrice('100');
    setPreviewDescription('');
    setGeneratingTeaser(false);
    setProgress(0);
    if (fileRef.current) fileRef.current.value = '';
  };

  const share = async () => {
    if (!file) return;
    if (!writable) {
      toast({
        title: t('verify_email.title'),
        description: t('verify_email.desc'),
        variant: 'destructive',
      });
      return;
    }
    if (isPaid) {
      const p = parseInt(price, 10);
      if (!priceCredits.includes(p)) {
        toast({
          title: t('price.invalid_title'),
          description: t('price.invalid_desc', { options: priceCredits.join(', ') }),
          variant: 'destructive',
        });
        return;
      }
      const teaser = previewDescription.trim();
      if (teaser.length < MIN_PREVIEW) {
        toast({
          title: t('preview_description.too_short_title'),
          description: t('preview_description.too_short_desc', { min: MIN_PREVIEW }),
          variant: 'destructive',
        });
        return;
      }
      if (teaser.length > MAX_PREVIEW) {
        toast({
          title: t('preview_description.too_long_title'),
          description: t('preview_description.too_long_desc', { max: MAX_PREVIEW }),
          variant: 'destructive',
        });
        return;
      }
    }
    setUploading(true);
    setProgress(0);
    try {
      // Phase 84 — big files use the chunked/resumable flow.
      const uploadFn = file.size > CHUNKED_THRESHOLD ? createPostChunked : createPost;
      const created = await uploadFn({
        file,
        caption,
        location,
        is_paid: isPaid,
        price: isPaid ? parseInt(price, 10) : 0,
        preview_description: isPaid ? previewDescription.trim() : '',
        media_width: mediaDims.width,
        media_height: mediaDims.height,
        onProgress: (p) => setProgress(p),
      });
      const processing = created?.approval_status === 'processing';
      toast({
        title: processing ? t('upload.processing_title') : t('upload.success_title'),
        description: processing ? t('upload.processing_desc') : t('upload.success_desc'),
      });
      await refresh();
      reset();
      navigate('/');
    } catch (err) {
      toast({
        title: t('upload.error_title'),
        description: err.response?.data?.detail || err.message || t('upload.error_default'),
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const kind = mediaKind;

  // Phase F8 — CREA Voice-First / Phase F8.1 — exactly 5 entries:
  // Racconto Audio, Racconto Testuale, Live Now, Chiamate, Programma una
  // Live. Foto removed from THIS screen only (legacy flow code below is
  // untouched). Each Live/Chiamate card reuses the EXACT Live Dashboard
  // handler logic (writable/isVip checks) and the EXACT same components.
  if (mode === null && !scheduling) {
    const isVip = !!user?.is_verified_creator;
    const requireWritable = (fn) => () => {
      if (!writable) {
        toast({
          title: tLives('dashboard.verify_email_first_title'),
          description: tLives('dashboard.verify_email_first_desc'),
          variant: 'destructive',
        });
        return;
      }
      fn();
    };
    const Choice = ({ icon: Icon, label, desc, onClick, testId, secondary = false, iconClass = '' }) => (
      <button
        onClick={onClick}
        className={`flex w-full items-center gap-4 rounded-xl border text-left transition-colors ${
          secondary
            ? 'border-border/60 bg-card/60 px-4 py-3 hover:border-border'
            : 'border-border bg-card px-4 py-4 hover:border-amber-500/50'
        }`}
        data-testid={testId}
      >
        <span
          className={`flex shrink-0 items-center justify-center rounded-full ${
            secondary ? 'h-10 w-10 bg-secondary text-muted-foreground' : `h-12 w-12 bg-amber-500/10 gold-text ${iconClass}`
          }`}
        >
          <Icon className={secondary ? 'h-5 w-5' : 'h-6 w-6'} strokeWidth={1.7} />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block font-bold ${secondary ? 'text-sm text-foreground/90' : 'text-base'}`}>{label}</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">{desc}</span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>
    );
    return (
      <div className="mx-auto max-w-[720px] px-4 pt-6 pb-24">
        <BackButton className="mb-3" />
        <div className="rounded-xl border border-border bg-card p-5" data-testid="create-chooser">
          <h2 className="text-xl font-bold">Crea</h2>
          <p className="mt-1 text-sm text-muted-foreground">Condividi una storia, una voce o avvia una conversazione.</p>
          <div className="mt-5 flex flex-col gap-3">
            <Choice icon={Mic} label="Racconto Audio" desc="Registra oppure carica un racconto con la tua voce." onClick={() => setMode('audio')} testId="create-choice-audio" />
            <Choice icon={BookOpen} label="Racconto Testuale" desc="Scrivi e pubblica un racconto." onClick={() => setMode('text')} testId="create-choice-text" />
            <Choice
              icon={Radio}
              label="Live Now"
              desc="Vai in diretta audio adesso."
              onClick={requireWritable(() => setGoLiveOpen(true))}
              testId="create-choice-live"
              iconClass="!bg-red-500/10 !text-red-500"
            />
            <Choice
              icon={Phone}
              label="Chiamate"
              desc="Gestisci le tue chiamate 1-to-1."
              onClick={() => (isVip ? navigate('/lives?tab=one-to-one') : setO2oGateOpen(true))}
              testId="create-choice-calls"
            />
            <Choice
              icon={Calendar}
              label="Programma una Live"
              desc="Vedi le tue Live future e programmane di nuove."
              onClick={requireWritable(() => setSearchParams({ mode: 'schedule' }))}
              testId="create-choice-schedule"
            />
          </div>
        </div>
        {/* Phase F8.1 — SAME components as the Live Dashboard, zero duplication */}
        <GoLiveNowDialog open={goLiveOpen} onOpenChange={setGoLiveOpen} kind="audio" />
        <OneToOneVipRequiredDialog open={o2oGateOpen} onOpenChange={setO2oGateOpen} />
      </div>
    );
  }
  if (mode === 'audio') return <RaccontoAudioForm onBack={() => setMode(null)} />;
  if (mode === 'text') return <RaccontoTextForm onBack={() => setMode(null)} />;
  // HOTFIX UI — Programma una Live: single operational hub (future lives
  // list + existing create/edit/cancel/start flows, all reused).
  if (scheduling) return <ScheduledLivesPanel onBack={() => setSearchParams({})} />;

  return (
    <div className="mx-auto max-w-[720px] px-4 pt-6 pb-24">
      <BackButton className="mb-3" />
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <button
            onClick={reset}
            disabled={uploading}
            className="text-sm text-foreground hover:text-foreground disabled:opacity-50"
          >
            {file ? t('header.discard') : t('header.cancel')}
          </button>
          <h2 className="text-base font-semibold">{t('header.title')}</h2>
          {file ? (
            <button
              onClick={share}
              disabled={uploading || !writable}
              className="text-sm font-semibold gold-text hover:underline disabled:opacity-50"
              data-testid="create-share-button"
            >
              {uploading ? t('header.sharing') : t('header.share')}
            </button>
          ) : (
            <span className="w-12" />
          )}
        </header>

        <ReadOnlyNotice action={t('read_only_action')} className="mx-4 mt-3" />

        {!file ? (
          <div className="flex min-h-[420px] flex-col items-center justify-center gap-4 px-6 py-12">
            <div className="flex gap-3 text-foreground">
              <ImageIcon className="h-14 w-14" strokeWidth={1.2} />
              {videoPostsEnabled && <Film className="h-14 w-14" strokeWidth={1.2} />}
              <Music className="h-14 w-14" strokeWidth={1.2} />
            </div>
            <p className="text-xl text-foreground">{t('picker.prompt')}</p>
            <Button
              onClick={() => fileRef.current?.click()}
              className="h-9 gold-gradient px-4 text-sm font-semibold"
            >
              {t('picker.select')}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept={videoPostsEnabled ? 'image/*,video/*,audio/*' : 'image/*,audio/*'}
              className="hidden"
              onChange={pick}
            />
            <p className="mt-2 text-center text-xs text-muted-foreground">
              {videoPostsEnabled ? t('picker.hint') : t('picker.hint_no_video')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="relative aspect-square bg-black">
              {kind === 'image' && (
                <img src={previewUrl} alt="preview" className="h-full w-full object-contain" />
              )}
              {kind === 'video' && (
                <video src={previewUrl} controls className="h-full w-full object-contain" />
              )}
              {kind === 'audio' && (
                <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-gradient-to-br from-neutral-100 to-neutral-200 px-6">
                  <div className="flex h-24 w-24 items-center justify-center rounded-full bg-card shadow-md">
                    <Music className="h-12 w-12 text-foreground" strokeWidth={1.6} />
                  </div>
                  <audio src={previewUrl} controls className="w-full max-w-md" />
                  <p className="truncate text-sm text-foreground">{file.name}</p>
                </div>
              )}
              {!uploading && (
                <button
                  onClick={reset}
                  className="absolute right-3 top-3 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70"
                  aria-label={t('remove_aria')}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex flex-col gap-3 p-4">
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder={t('caption_placeholder')}
                className="min-h-[150px] resize-none border-0 p-0 text-sm focus-visible:ring-0"
                maxLength={MAX_CAPTION}
                disabled={uploading}
                data-testid="create-caption-textarea"
              />
              <div
                className={`text-right text-xs tabular-nums ${
                  caption.length >= MAX_CAPTION * 0.95 ? 'gold-text' : 'text-muted-foreground'
                }`}
                data-testid="create-caption-counter"
              >
                {caption.length.toLocaleString()} / {MAX_CAPTION.toLocaleString()}
              </div>
              <div className="border-t border-border pt-3">
                <label className="mb-1 flex items-center gap-1 text-xs font-semibold text-foreground">
                  <MapPin className="h-3.5 w-3.5" /> {t('location.label')}
                </label>
                <Input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder={t('location.placeholder')}
                  disabled={uploading}
                  className="h-8 text-sm"
                />
              </div>

              {/* Monetization */}
              <div className="rounded-lg border border-border bg-background p-3" data-testid="monetization-section">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-foreground">
                  <Sparkles className="h-3.5 w-3.5" /> {t('monetization.title')}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIsPaid(false)}
                    disabled={uploading}
                    className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                      !isPaid
                        ? 'border-black bg-black text-white'
                        : 'border-border bg-card text-foreground hover:bg-secondary'
                    }`}
                  >
                    {t('monetization.free')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!canMonetize) {
                        toast({
                          title: needsCreatorActivation
                            ? t('monetization.toast_become_vip_title')
                            : t('monetization.toast_suspended_title'),
                          description: needsCreatorActivation
                            ? t('monetization.toast_become_vip_desc')
                            : t('monetization.toast_suspended_desc'),
                        });
                        return;
                      }
                      setIsPaid(true);
                    }}
                    disabled={uploading}
                    aria-disabled={!canMonetize}
                    className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition-colors ${
                      !canMonetize
                        ? 'cursor-not-allowed border-border bg-secondary text-muted-foreground'
                        : isPaid
                          ? 'border-amber-600 bg-amber-500 text-white'
                          : 'border-border bg-card text-foreground hover:bg-secondary'
                    }`}
                    data-testid="create-paid-toggle"
                  >
                    {canMonetize ? t('monetization.paid') : (
                      <span className="inline-flex items-center gap-1.5">
                        <Lock className="h-3.5 w-3.5" /> {t('monetization.paid_vip')}
                      </span>
                    )}
                  </button>
                </div>
                {!canMonetize && (
                  <button
                    type="button"
                    onClick={() => navigate(needsCreatorActivation ? '/profile' : '/contact')}
                    className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold gold-text hover:bg-amber-500/20"
                    data-testid="paid-locked-cta"
                  >
                    <Crown className="h-3.5 w-3.5" />
                    {needsCreatorActivation
                      ? t('monetization.cta_become_vip')
                      : t('monetization.cta_suspended')}
                  </button>
                )}
                {isPaid && canMonetize && (
                  <div className="mt-3 space-y-3">
                    <div data-testid="post-price-block">
                      <label className="mb-1 block text-xs font-semibold text-foreground">
                        {t('price.label')}
                      </label>
                      <select
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        disabled={uploading}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                        data-testid="post-price-select"
                      >
                        {priceCredits.map((c) => (
                          <option key={c} value={c}>
                            {c === 0 ? 'Gratis · €0.00' : `${c} Credits · €${creditsToEur(c).toFixed(2)}`}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        <Trans
                          i18nKey="price.hint"
                          ns="create"
                          values={{ amount: fmtCredits(price) }}
                          components={{ strong: <span className="font-semibold" /> }}
                        />
                      </p>
                    </div>

                    {/* Phase 18.2 — mandatory public teaser shown on the paywall.
                        Helps buyers decide; reduces refund disputes.
                        Phase 18.3 — optional AI-assist via the gold wand. */}
                    <div data-testid="preview-description-block">
                      <div className="mb-1 flex items-center justify-between">
                        <label className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                          <Lock className="h-3.5 w-3.5 gold-text" /> {t('preview_description.label')}
                          <span className="gold-text">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={async () => {
                            if (generatingTeaser || uploading) return;
                            setGeneratingTeaser(true);
                            try {
                              const kindNow = file ? getMediaKind(file) : 'image';
                              const { teaser } = await generateTeaser({
                                caption: caption || previewDescription,
                                media_type: kindNow || 'image',
                                price: parseFloat(price) || 0,
                              });
                              setPreviewDescription(teaser.slice(0, MAX_PREVIEW));
                              toast({
                                title: t('preview_description.ai_success_title'),
                                description: t('preview_description.ai_success_desc'),
                              });
                            } catch (e) {
                              const detail = e.response?.data?.detail || '';
                              toast({
                                title: t('preview_description.ai_error_title'),
                                description: detail.split('|').pop() || t('preview_description.ai_error_default'),
                                variant: 'destructive',
                              });
                            } finally {
                              setGeneratingTeaser(false);
                            }
                          }}
                          disabled={generatingTeaser || uploading}
                          className="group inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold gold-text transition hover:bg-amber-500/20 disabled:opacity-50"
                          data-testid="preview-description-ai-btn"
                          title={t('preview_description.ai_button_title')}
                        >
                          {generatingTeaser ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Wand2 className="h-3 w-3 transition group-hover:scale-110" />
                          )}
                          {generatingTeaser ? t('preview_description.ai_generating') : t('preview_description.ai_assist')}
                        </button>
                      </div>
                      <Textarea
                        value={previewDescription}
                        onChange={(e) => setPreviewDescription(e.target.value.slice(0, MAX_PREVIEW))}
                        placeholder={t('preview_description.placeholder')}
                        className="min-h-[80px] resize-none border-border bg-background text-sm"
                        maxLength={MAX_PREVIEW}
                        disabled={uploading || generatingTeaser}
                        data-testid="create-preview-description-textarea"
                      />
                      <div className="mt-1 flex items-center justify-between text-[11px]">
                        <span className="text-muted-foreground">
                          {t('preview_description.hint')}
                        </span>
                        <span
                          className={`tabular-nums ${
                            previewDescription.length < MIN_PREVIEW
                              ? 'text-red-400'
                              : previewDescription.length >= MAX_PREVIEW * 0.9
                                ? 'gold-text'
                                : 'text-muted-foreground'
                          }`}
                          data-testid="preview-description-counter"
                        >
                          {previewDescription.length} / {MAX_PREVIEW}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {uploading && (
                <div className="mt-2">
                  <Progress value={progress} className="h-2" />
                  <p className="mt-1 text-xs text-muted-foreground">{t('upload.uploading', { percent: progress })}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatePage;
